import axios, { AxiosPromise, AxiosRequestConfig } from 'axios'
import qs from 'qs'

import { FileCategory } from 'enums'
import { InvoiceType } from 'generated/graphql'
import { getOidcStorage } from 'helpers'

const commonHeaders = () => {
  const storage = getOidcStorage()

  return {
    Authorization: storage?.tokens?.accessToken ? `Bearer ${storage.tokens.accessToken}` : '',
  }
}

const baseUrl = process.env.REACT_APP_GRAPHQL_URL

export function DownloadBookingInvoice(bookingInvoiceId): AxiosPromise<any> {
  const options = {
    method: 'get',
    url: `${baseUrl}/api/reports/DownloadBookingInvoiceForXero`,
    params: {
      id: bookingInvoiceId,
    },
    headers: { ...commonHeaders(), 'Content-Type': 'text/csv' },
    responseType: 'blob',
  } as AxiosRequestConfig

  return axios(options)
}

export function DownloadInvoiceByInvoiceNumber(invoiceNumber): AxiosPromise<any> {
  const options = {
    method: 'get',
    url: `${baseUrl}/api/reports/DownloadInvoiceByInvoiceNumber`,
    params: {
      invoiceNumber: invoiceNumber,
    },
    headers: { ...commonHeaders(), 'Content-Type': 'text/csv' },
    responseType: 'blob',
  } as AxiosRequestConfig

  return axios(options)
}

export function DownloadRunsheet(bookingId): AxiosPromise<any> {
  const options = {
    method: 'post',
    url: `${baseUrl}/api/reports/DownloadRunsheet`,
    params: {
      bookingId: Number(bookingId),
    },
    headers: { ...commonHeaders() },
    responseType: 'blob',
  } as AxiosRequestConfig

  return axios(options)
}

export function DownloadBookingInvoices(
  startDate: Date,
  endDate: Date,
  type: InvoiceType,
): AxiosPromise<any> {
  const options = {
    method: 'get',
    url: `${baseUrl}/api/reports/DownloadBookingInvoicesForXero`,
    params: { startDate, endDate, type },
    headers: { ...commonHeaders(), 'Content-Type': 'text/csv' },
    responseType: 'blob',
  } as AxiosRequestConfig

  return axios(options)
}

export function DownloadDriverInvoice(driverInvoiceId): AxiosPromise<any> {
  const options = {
    method: 'get',
    url: `${baseUrl}/api/reports/DownloadDriverInvoiceForXero`,
    params: { id: driverInvoiceId },
    headers: { ...commonHeaders(), 'Content-Type': 'text/csv' },
    responseType: 'blob',
  } as AxiosRequestConfig

  return axios(options)
}

export function DownloadSelectedDriverInvoicesReport(
  invoiceFileType,
  invoiceIds,
): AxiosPromise<any> {
  const options = {
    method: 'get',
    url: `${baseUrl}/api/reports/DownloadSelectedDriverInvoicesReport`,
    params: { invoiceFileType, invoiceIds },
    headers: { ...commonHeaders() },
    responseType: 'blob',
    paramsSerializer: (params) => {
      return qs.stringify(params)
    },
  } as AxiosRequestConfig

  return axios(options)
}

export function DownloadAllDriverInvoicesReport(invoiceFileType, driverId): AxiosPromise<any> {
  const options = {
    method: 'get',
    url: `${baseUrl}/api/reports/DownloadAllDriverInvoicesReport`,
    params: { invoiceFileType, driverId },
    headers: { ...commonHeaders() },
    responseType: 'blob',
    paramsSerializer: (params) => {
      return qs.stringify(params)
    },
  } as AxiosRequestConfig

  return axios(options)
}

export function DownloadAccountPayableInvoiceReport(accountPayableRecordId): AxiosPromise<any> {
  const options = {
    method: 'get',
    url: `${baseUrl}/api/reports/DownloadAccountPayableInvoiceReport`,
    params: { accountPayableRecordId },
    headers: { ...commonHeaders() },
    responseType: 'blob',
    paramsSerializer: (params) => {
      return qs.stringify(params)
    },
  } as AxiosRequestConfig

  return axios(options)
}

export function DownloadCustomerIncomeReport(type, data): AxiosPromise<any> {
  const options = {
    method: 'post',
    url: `${baseUrl}/api/reports/DownloadCustomerIncomeReport`,
    params: { type },
    data,
    headers: { ...commonHeaders() },
    responseType: 'blob',
  } as AxiosRequestConfig

  return axios(options)
}

export function DownloadMyIncomeReport(type, data): AxiosPromise<any> {
  const options = {
    method: 'post',
    url: `${baseUrl}/api/reports/DownloadMyIncomeReport`,
    params: { type },
    data,
    headers: { ...commonHeaders() },
    responseType: 'blob',
  } as AxiosRequestConfig

  return axios(options)
}

export function DownloadCustomerContactListReport(type, data): AxiosPromise<any> {
  const options = {
    method: 'post',
    url: `${baseUrl}/api/reports/DownloadCustomerContactListReport`,
    params: { type },
    data,
    headers: { ...commonHeaders() },
    responseType: 'blob',
  } as AxiosRequestConfig

  return axios(options)
}

export function DownloadDriverListReport(type, data): AxiosPromise<any> {
  const options = {
    method: 'post',
    url: `${baseUrl}/api/reports/DownloadDriverListReport`,
    params: { type },
    data,
    headers: { ...commonHeaders() },
    responseType: 'blob',
  } as AxiosRequestConfig

  return axios(options)
}

export function DownloadSalesCommissionReport(type, data): AxiosPromise<any> {
  const options = {
    method: 'post',
    url: `${baseUrl}/api/reports/DownloadSalesCommissionReport`,
    params: { type },
    data,
    headers: { ...commonHeaders() },
    responseType: 'blob',
  } as AxiosRequestConfig

  return axios(options)
}

export function DownloadProofOfDeliveryReport(type, data): AxiosPromise<any> {
  const options = {
    method: 'post',
    url: `${baseUrl}/api/reports/DownloadProofOfDeliveryReport`,
    params: { type },
    data,
    headers: { ...commonHeaders() },
    responseType: 'blob',
  } as AxiosRequestConfig

  return axios(options)
}

export function DownloadSupplierPaymentOverviewReport(type, data): AxiosPromise<any> {
  const options = {
    method: 'post',
    url: `${baseUrl}/api/reports/DownloadSupplierPaymentOverviewReport`,
    params: { type },
    data,
    headers: { ...commonHeaders() },
    responseType: 'blob',
  } as AxiosRequestConfig

  return axios(options)
}

export function DownloadFile(fileId): AxiosPromise<any> {
  const options = {
    method: 'get',
    url: `${baseUrl}/api/files/download`,
    params: { fileId },
    headers: { ...commonHeaders() },
    responseType: 'blob',
  } as AxiosRequestConfig

  return axios(options)
}

export function DownloadFileAsArrayBuffer(fileId): AxiosPromise<any> {
  const options = {
    method: 'get',
    url: `${baseUrl}/api/files/download`,
    params: { fileId },
    headers: { ...commonHeaders() },
    responseType: 'arraybuffer',
  } as AxiosRequestConfig

  return axios(options)
}

export function DownloadProofOfDeliveryZip(
  trackingHash: string,
  documentZipType: 'collection' | 'delivery' | 'combined',
): AxiosPromise<any> {
  const options = {
    method: 'get',
    url: `${baseUrl}/api/files/DownloadProofOfDeliveryZip`,
    params: { trackingHash, documentZipType },
    headers: { ...commonHeaders() },
    responseType: 'blob',
  } as AxiosRequestConfig

  return axios(options)
}

export function UploadBookingAttachment(bookingId: number, data: any): AxiosPromise<Array<number>> {
  const options = {
    method: 'post',
    url: `${baseUrl}/api/files/UploadBookingAttachment`,
    params: { bookingId },
    data: data,
    headers: { ...commonHeaders() },
  } as AxiosRequestConfig

  return axios(options)
}

export function ImportDriversFile(data: any): AxiosPromise<string[]> {
  const options = {
    method: 'post',
    url: `${baseUrl}/api/files/UploadDriverListImport`,
    data: data,
    headers: { ...commonHeaders() },
  } as AxiosRequestConfig

  return axios(options)
}

export function ImportBookingsFile(data: any): AxiosPromise<string[]> {
  const options = {
    method: 'post',
    url: `${baseUrl}/api/files/UploadBookingListImport`,
    data: data,
    headers: { ...commonHeaders() },
  } as AxiosRequestConfig

  return axios(options)
}

export function ImportDriversCodeFile(data: any): AxiosPromise<string[]> {
  const options = {
    method: 'post',
    url: `${baseUrl}/api/files/UploadDriversXeroCode`,
    data: data,
    headers: { ...commonHeaders() },
  } as AxiosRequestConfig

  return axios(options)
}

export function ExportDriversCodeFile(): AxiosPromise<string[]> {
  const options = {
    method: 'get',
    url: `${baseUrl}/api/Reports/DownloadDriversXeroCode`,
    headers: { ...commonHeaders(), 'Content-Type': 'text/csv' },
    responseType: 'blob',
  } as AxiosRequestConfig

  return axios(options)
}

export function UploadDriverInvoices(fileData): AxiosPromise<Array<any>> {
  const options = {
    method: 'post',
    url: `${baseUrl}/api/files/UploadExternalDriverInvoice`,
    data: fileData,
    headers: { ...commonHeaders() },
  } as AxiosRequestConfig

  return axios(options)
}

export function UploadBookingAddressAttachment(
  bookingAddressId: number,
  data: any,
  category: FileCategory | null | undefined = FileCategory.BookingDocument,
): AxiosPromise<Array<number>> {
  const options = {
    method: 'post',
    url: `${baseUrl}/api/files/UploadBookingAddressAttachment`,
    params: { id: bookingAddressId, category: category },
    data,
    headers: { ...commonHeaders() },
  } as AxiosRequestConfig
  return axios(options)
}

export function UploadCustomerLogo(customerId: number, data: any): AxiosPromise<Array<any>> {
  const options = {
    method: 'POST',
    url: `${baseUrl}/api/files/UploadCustomerLogo`,
    params: { customerId },
    data: data,
    headers: { ...commonHeaders() },
  } as AxiosRequestConfig

  return axios(options)
}

export function UploadUserAvatar(userId: number, data: any): AxiosPromise<Array<number>> {
  const options = {
    method: 'POST',
    url: `${baseUrl}/api/files/UploadUserAvatar`,
    params: { userId },
    data: data,
    headers: { ...commonHeaders() },
  } as AxiosRequestConfig

  return axios(options)
}

export function UploadProfileAvatar(data: any): AxiosPromise<Array<any>> {
  const options = {
    method: 'POST',
    url: `${baseUrl}/api/files/UploadProfileAvatar`,
    data: data,
    headers: { ...commonHeaders() },
  } as AxiosRequestConfig

  return axios(options)
}

export function UploadTariffIcon(tariffIconId: number, data: any): AxiosPromise<Array<any>> {
  const options = {
    method: 'POST',
    url: `${baseUrl}/api/files/UploadTariffIcon`,
    params: { tariffIconId },
    data: data,
    headers: { ...commonHeaders() },
  } as AxiosRequestConfig

  return axios(options)
}

export function UploadBanner(bannerId: number, data: any): AxiosPromise<Array<any>> {
  const options = {
    method: 'POST',
    url: `${baseUrl}/api/files/UploadPromotion`,
    params: { promotionId: bannerId },
    data: data,
    headers: { ...commonHeaders() },
  } as AxiosRequestConfig

  return axios(options)
}

export function UploadPotentialBookingFile(data: any): AxiosPromise<string[]> {
  const options = {
    method: 'post',
    url: `${baseUrl}/api/files/UploadPotentialBookingFile`,
    data: data,
    headers: { ...commonHeaders() },
  } as AxiosRequestConfig

  return axios(options)
}

export function UploadSmartPayCsv(data: any): AxiosPromise<any> {
  const options = {
    method: 'post',
    url: `${baseUrl}/api/files/UploadSmartPayCsv`,
    data: data,
    headers: { ...commonHeaders() },
  } as AxiosRequestConfig

  return axios(options)
}

export function GenerateSmartPayExportCsvFromInputFile(data: any): AxiosPromise<any> {
  const options = {
    method: 'post',
    url: `${baseUrl}/api/files/GenerateSmartPayExportCsvFromInputFile`,
    data: data,
    headers: { ...commonHeaders() },
  } as AxiosRequestConfig

  return axios(options)
}

export function UploadLegalDocumentFile(
  data: FormData,
): AxiosPromise<{ absoluteUri: string; originalFileName: string; size: number }> {
  const options = {
    method: 'POST',
    url: `${baseUrl}/api/files/UploadLegalDocumentFile`,
    data: data,
    headers: { ...commonHeaders() },
  } as AxiosRequestConfig

  return axios(options)
}

export function DownloadLegalDocumentFile(uri: string, fileName: string): AxiosPromise<Blob> {
  const options = {
    method: 'GET',
    url: `${baseUrl}/api/files/DownloadLegalDocumentFile`,
    params: { uri, fileName },
    headers: { ...commonHeaders() },
    responseType: 'blob',
  } as AxiosRequestConfig

  return axios(options)
}
